/**
 * AudioManager — centralized sound system for the Babylon.js port.
 *
 * Listens to gameEvents (the global EventBus) and plays appropriate
 * Babylon.js Sound instances. Fully decoupled from gameplay logic:
 * game systems never call audio directly.
 *
 * Usage:
 *   const audio = new AudioManager(scene, 'LiminalAtrium');
 *   audio.startAmbient();
 *   // ...game emits events, AudioManager reacts automatically.
 *   audio.dispose();  // when leaving the scene
 */
import { Scene } from "@babylonjs/core/scene";
import "@babylonjs/core/Audio/audioSceneComponent";
import { Sound } from "@babylonjs/core/Audio/sound";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { LEVEL_AUDIO } from "../config/GameConfig";
import { speciesSfxForCalm, elderChimeKey, abilityBlockedSfxKey } from "./audio/SfxEventMap";
import { effectiveVolume, duckedMusic } from "./audio/VolumeBus";
import { SaveSystem } from "./SaveSystem";

/** SFX filename map (event key -> filename in assets/audio/). Exported for completeness tests. */
export const SFX_FILES: Record<string, string> = {
  jump: "jump.wav",
  land: "land.wav",
  "ability-activate": "ability-activate.wav",
  "ability-deactivate": "ability-deactivate.wav",
  purr: "purr.wav",
  "dial-spin": "dial-spin.wav",
  "dial-aligned": "dial-aligned.wav",
  "pressure-plate": "pressure-plate.wav",
  "crystal-charge": "crystal-charge.wav",
  "portal-open": "portal-open.wav",
  "puzzle-solved": "puzzle-solved.wav",
  "rhythm-hit": "rhythm-hit.wav",
  "rhythm-miss": "rhythm-miss.wav",
  "level-complete": "level-complete.wav",
  "elder-chime": "elder-chime.wav",
  "resonant-drop": "resonant-drop.wav",
  "chronos-bubble": "chronos-bubble.wav",
  // Canon p.7 — Basil-operated gramophone plays a short sleepy melody
  // that lulls the Guardian Dog back to sleep. ~3.5s bell-tone arpeggio.
  "gramophone-lullaby": "gramophone-lullaby.wav",
  // Task 5 — per-species NPC calm SFX + environment/ability SFX
  "chain-shatter": "chain-shatter.wav",
  "crane-creak": "crane-creak.wav",
  "fox-growl": "fox-growl.wav",
  "elder-gus-chime": "elder-gus-chime.wav",
  "baking-ding": "baking-ding.wav",
  "crow-caw": "crow-caw.wav",
  "squirrel-chitter": "squirrel-chitter.wav",
  "kitten-mew": "kitten-mew.wav",
  "opossum-squeak": "opossum-squeak.wav",
  // Task 7 — footstep, shadow-slip, damage SFX
  footstep: "footstep.wav",
  "shadow-slip": "shadow-slip.wav",
  damage: "damage.wav",
};

const AUDIO_BASE_PATH = "assets/audio/";
const DEFAULT_SFX_POOL_SIZE = 2;
/** Hard cap on concurrent one-shot SFX voices (ambient + purr loops are exempt). */
const MAX_CONCURRENT_SFX = 16;
/**
 * Footstep voices reserved from the MAX_CONCURRENT_SFX cap.
 * Footsteps use a dedicated pool and only consume a voice slot if fewer than
 * this many non-footstep voices are already active — i.e., footsteps are
 * deprioritized so gameplay-critical SFX (puzzle-solved, damage, etc.) never
 * get evicted by high-cadence footstep bursts.
 */
const FOOTSTEP_MAX_VOICES = 3;

const SFX_POOL_SIZES: Record<string, number> = {
  jump: 3,
  land: 3,
  "dial-spin": 3,
  "pressure-plate": 2,
  "rhythm-hit": 2,
  "rhythm-miss": 2,
  // Larger pool for high-cadence footsteps; deprioritized in cap logic.
  footstep: 4,
};

export class AudioManager {
  private scene: Scene;
  private soundPools: Map<string, Sound[]> = new Map();
  private ambientLoop: Sound | null = null;
  private purrLoop: Sound | null = null;
  private synergyLoop: Sound | null = null;
  private activeVoices: { sound: Sound; startedAt: number }[] = [];

  /** Volume controls (0-1). */
  private sfxVolume = 0.5;
  private ambientVolume = 0.3;
  private synergyTargetVolume = 0.0;

  /**
   * Master volume scalar applied on top of every channel.
   * Default 1.0. Task 11 will read this from the persistence store at
   * construction before loadSounds() — see TASK-11-HOOK below.
   */
  private masterVolume = 1.0;

  /**
   * Music bus volume [0..1]. The music layer uses the per-level ambient
   * (or gramophone-lullaby) as a placeholder bed until composed tracks land.
   *
   * PLACEHOLDER: No composed soundtrack exists yet — music layer reuses
   * the ambient loop file as a placeholder music bed. See design-backlog.md
   * "visual/audio ship polish" for the composed-soundtrack milestone.
   *
   * Default 0.4. Task 11 will read this from the persistence store at
   * construction before loadSounds() — see TASK-11-HOOK below.
   */
  private musicVolume = 0.4;

  /**
   * Whether music is currently ducked (during ELDER_ENCOUNTER / LEVEL_COMPLETE).
   * Restored on the next non-ducking event or explicit setMusicVolume call.
   */
  private musicDucked = false;

  /**
   * Dedicated music loop: reuses the per-level ambient file as a placeholder
   * music bed (same file, separate Sound so SFX + ambient channels remain
   * independent). Null when no level audio is configured.
   *
   * PLACEHOLDER gap: no composed music track; see design-backlog.md.
   */
  private musicLoop: Sound | null = null;

  /** Unsubscribe handles for event listeners. */
  private unsubscribers: Array<() => void> = [];

  /** True after dispose() — guards late setTimeout callbacks (e.g. startAmbient). */
  private disposed = false;

  /** Keys for which a missing-file warning has already been emitted (warn-once). */
  private warnedMissingFiles = new Set<string>();

  constructor(scene: Scene, levelKey?: string) {
    this.scene = scene;
    // Read persisted volumes before loadSounds() so the first audio frame
    // uses the player's saved preferences rather than hard-coded defaults.
    const saved = new SaveSystem().loadVolumes();
    this.masterVolume = saved.master;
    this.sfxVolume    = saved.sfx;
    this.musicVolume  = saved.music;
    this.loadSounds(levelKey);
    this.registerEventListeners();
  }

  // ─── Public API ──────────────────────────────────────────────────────

  /** Start the ambient loop for the current level. No-ops if already disposed. */
  startAmbient(): void {
    if (this.disposed) return;
    if (this.ambientLoop && !this.ambientLoop.isPlaying) {
      this.ambientLoop.play();
    }
    // Start the music bed alongside ambient so all 4 scenes get music
    // automatically via the single startAmbient() call they already make.
    this.startMusic();
  }

  /** Stop the ambient loop. */
  stopAmbient(): void {
    if (this.ambientLoop?.isPlaying) {
      this.ambientLoop.stop();
    }
  }

  /**
   * Start the dedicated music bed loop. Disposed-guarded; no-ops if already
   * playing or if musicLoop failed to construct (NullEngine / missing file).
   * Called automatically by startAmbient() so scenes need no extra call.
   */
  startMusic(): void {
    if (this.disposed) return;
    if (this.musicLoop && !this.musicLoop.isPlaying) {
      try {
        this.musicLoop.play();
      } catch {
        // NullEngine or missing AudioContext — ignore silently.
      }
    }
  }

  /** Stop the music bed loop. */
  stopMusic(): void {
    if (this.musicLoop?.isPlaying) {
      try {
        this.musicLoop.stop();
      } catch {
        // NullEngine — ignore silently.
      }
    }
  }

  /** Stop all currently playing sounds. */
  stopAll(): void {
    for (const pool of this.soundPools.values()) {
      for (const sound of pool) {
        if (sound.isPlaying) {
          this.releaseVoice(sound);
          sound.stop();
        }
      }
    }
    this.ambientLoop?.stop();
    this.purrLoop?.stop();
  }

  /** Set SFX volume (0-1). Applies to future plays. */
  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    for (const pool of this.soundPools.values()) {
      for (const sound of pool) {
        sound.setVolume(effectiveVolume(this.masterVolume, this.sfxVolume));
      }
    }
  }

  /** Set ambient volume (0-1). */
  setAmbientVolume(volume: number): void {
    this.ambientVolume = Math.max(0, Math.min(1, volume));
    if (this.ambientLoop) {
      this.ambientLoop.setVolume(effectiveVolume(this.masterVolume, this.ambientVolume));
    }
  }

  /**
   * Set master volume (0-1). Re-applies to ALL pooled Sounds, loops, and the
   * music layer, preserving existing per-channel and per-sound ratios.
   */
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.applyAllVolumes();
  }

  /**
   * Set music bus volume (0-1). Re-applies to the music layer (with current
   * ducking state), but does NOT affect SFX or ambient channels.
   */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    this.applyMusicVolume();
  }

  /**
   * Fan out master + channel volumes to every managed Sound.
   * Called by setMasterVolume and whenever ducking state changes.
   */
  private applyAllVolumes(): void {
    // SFX pools
    for (const pool of this.soundPools.values()) {
      for (const sound of pool) {
        sound.setVolume(effectiveVolume(this.masterVolume, this.sfxVolume));
      }
    }
    // Purr loop preserves its 0.6 base ratio relative to sfxVolume
    if (this.purrLoop) {
      this.purrLoop.setVolume(effectiveVolume(this.masterVolume, this.sfxVolume, 0.6));
    }
    // Ambient loop
    if (this.ambientLoop) {
      this.ambientLoop.setVolume(effectiveVolume(this.masterVolume, this.ambientVolume));
    }
    // Music loop (with ducking)
    this.applyMusicVolume();
    // synergyLoop volume is managed by the crossfader in the render loop;
    // its target ceiling is ambientVolume*1.5 — re-clamp the target so a
    // master change takes effect on the next crossfader tick.
    this.synergyTargetVolume = Math.min(
      this.synergyTargetVolume,
      effectiveVolume(this.masterVolume, this.ambientVolume, 1.5),
    );
  }

  /** Apply the music bus volume (with current ducking) to musicLoop. */
  private applyMusicVolume(): void {
    if (!this.musicLoop) return;
    const vol = duckedMusic(this.musicVolume, this.musicDucked);
    this.musicLoop.setVolume(effectiveVolume(this.masterVolume, vol));
  }

  /** Duck / un-duck the music bus. */
  private setMusicDucked(ducked: boolean): void {
    this.musicDucked = ducked;
    this.applyMusicVolume();
  }

  /** Clean up all sounds and event listeners. Call when leaving the scene. */
  dispose(): void {
    this.disposed = true;
    this.stopAll();

    for (const unsub of this.unsubscribers) {
      unsub();
    }
    this.unsubscribers = [];

    for (const pool of this.soundPools.values()) {
      for (const sound of pool) {
        sound.dispose();
      }
    }
    this.soundPools.clear();
    this.activeVoices = [];

    this.ambientLoop?.dispose();
    this.ambientLoop = null;

    this.purrLoop?.dispose();
    this.purrLoop = null;

    this.synergyLoop?.dispose();
    this.synergyLoop = null;

    this.musicLoop?.dispose();
    this.musicLoop = null;
  }

  // ─── Private ─────────────────────────────────────────────────────────

  private loadSounds(levelKey?: string): void {
    // Load all SFX — construction is best-effort; a missing file logs once via
    // a fire-and-forget HEAD fetch rather than throwing synchronously.
    for (const [key, filename] of Object.entries(SFX_FILES)) {
      const poolSize = SFX_POOL_SIZES[key] ?? DEFAULT_SFX_POOL_SIZE;
      const pool: Sound[] = [];
      for (let i = 0; i < poolSize; i++) {
        try {
          const sound = new Sound(
            `${key}_${i}`,
            `${AUDIO_BASE_PATH}${filename}`,
            this.scene,
            null,
            { volume: this.sfxVolume, autoplay: false }
          );
          sound.onEndedObservable.add(() => {
            this.releaseVoice(sound);
          });
          pool.push(sound);
        } catch {
          // Construction error (e.g. no AudioContext) — warn once, skip entry.
          if (!this.warnedMissingFiles.has(key)) {
            this.warnedMissingFiles.add(key);
            console.warn(`[AudioManager] Failed to create Sound for key "${key}" (${AUDIO_BASE_PATH}${filename})`);
          }
        }
      }
      this.soundPools.set(key, pool);
      // Non-blocking 404 probe — warns once if the asset is absent at runtime.
      this.probeAudioFile(key, `${AUDIO_BASE_PATH}${filename}`);
    }

    // Purr loop (separate because it loops)
    this.purrLoop = new Sound(
      "purr-loop",
      `${AUDIO_BASE_PATH}purr.wav`,
      this.scene,
      null,
      { volume: this.sfxVolume * 0.6, autoplay: false, loop: true }
    );

    // Synergy loop (separate because it loops and dynamically crossfades)
    this.synergyLoop = new Sound(
      "synergy-loop",
      `${AUDIO_BASE_PATH}gramophone-lullaby.wav`,
      this.scene,
      null,
      { volume: 0.0, autoplay: false, loop: true }
    );

    // Ambient loop for current level (config-driven via LEVEL_AUDIO)
    const levelAudio = levelKey ? LEVEL_AUDIO[levelKey] : undefined;
    const ambientFile = levelAudio?.ambient;
    if (ambientFile) {
      const ambientKey = `ambient:${levelKey}`;
      this.ambientLoop = new Sound(
        "ambient-loop",
        `${AUDIO_BASE_PATH}${ambientFile}`,
        this.scene,
        null,
        { volume: this.ambientVolume, autoplay: false, loop: true }
      );
      // Non-blocking 404 probe for the ambient file — warns once, never throws.
      this.probeAudioFile(ambientKey, `${AUDIO_BASE_PATH}${ambientFile}`);
    } else if (levelKey) {
      // levelKey provided but no LEVEL_AUDIO entry — warn once.
      const missingKey = `ambient:${levelKey}`;
      if (!this.warnedMissingFiles.has(missingKey)) {
        this.warnedMissingFiles.add(missingKey);
        console.warn(`[AudioManager] No LEVEL_AUDIO entry for level "${levelKey}" — ambient will be silent.`);
      }
    }

    // Music layer — dedicated synthesized music bed (music-bed.wav).
    // Uses a separate file from the level ambient so the two never double each
    // other (ambient = noise-based texture; music bed = sine-partial pad/drone).
    // NullEngine-safe: wrapped in try/catch like the SFX pool entries.
    const musicFile = "music-bed.wav";
    try {
      this.musicLoop = new Sound(
        "music-loop",
        `${AUDIO_BASE_PATH}${musicFile}`,
        this.scene,
        null,
        {
          volume: effectiveVolume(this.masterVolume, duckedMusic(this.musicVolume, this.musicDucked)),
          autoplay: false,
          loop: true,
        }
      );
      // Non-blocking probe so missing file shows a clear warning (not a crash).
      this.probeAudioFile("music-loop", `${AUDIO_BASE_PATH}${musicFile}`);
    } catch {
      if (!this.warnedMissingFiles.has("music-loop")) {
        this.warnedMissingFiles.add("music-loop");
        console.warn(`[AudioManager] Failed to create music loop Sound (${musicFile}) — music will be silent.`);
      }
    }
  }

  /**
   * Fire-and-forget HEAD request to detect missing audio files at runtime.
   * Logs console.warn once per key if the file returns a non-OK status.
   * Safe to call in environments without fetch (e.g. NullEngine tests) — swallows all errors.
   */
  private probeAudioFile(key: string, url: string): void {
    if (typeof fetch === "undefined") return;
    if (this.warnedMissingFiles.has(key)) return;
    fetch(url, { method: "HEAD" }).then((res) => {
      if (!res.ok && !this.warnedMissingFiles.has(key)) {
        this.warnedMissingFiles.add(key);
        console.warn(`[AudioManager] Audio file not found (${res.status}): ${url}`);
      }
    }).catch(() => {
      // Network unavailable or fetch blocked — suppress silently.
    });
  }

  private play(key: string): void {
    this.playWithOptions(key, {});
  }

  /**
   * Play a pooled SFX with optional per-call gain and playback rate overrides.
   *
   * For footstep keys the voice cap check is relaxed: footsteps only steal a
   * slot if fewer than FOOTSTEP_MAX_VOICES footstep voices are active AND the
   * total active count is below MAX_CONCURRENT_SFX. This prevents high-cadence
   * footstep bursts from evicting gameplay-critical voices.
   *
   * @param gainMultiplier  Multiplied against sfxVolume (default 1.0).
   * @param playbackRate    Playback speed (1.0 = normal; <1 = lower/slower).
   */
  private playWithOptions(
    key: string,
    { gainMultiplier = 1.0, playbackRate = 1.0 }: { gainMultiplier?: number; playbackRate?: number }
  ): void {
    const pool = this.soundPools.get(key);
    if (!pool || pool.length === 0) {
      return;
    }

    // Footstep deprioritization: skip if too many footstep voices already active.
    if (key === "footstep") {
      const activeFootstepCount = this.activeVoices.filter(
        (v) => pool.includes(v.sound)
      ).length;
      if (activeFootstepCount >= FOOTSTEP_MAX_VOICES) {
        return;
      }
    }

    let sound = pool.find((entry) => !entry.isPlaying) ?? pool[0];
    if (sound.isPlaying) {
      this.releaseVoice(sound);
      sound.stop();
    }

    // For non-footstep keys enforce the global voice cap by evicting the oldest.
    if (key !== "footstep" && this.activeVoices.length >= MAX_CONCURRENT_SFX) {
      const oldest = this.activeVoices.shift();
      if (oldest) {
        oldest.sound.stop();
      }
    }

    this.activeVoices.push({ sound, startedAt: performance.now() });
    sound.setVolume(this.sfxVolume * gainMultiplier);
    if (playbackRate !== 1.0) {
      // Babylon 9.x public API: Sound.setPlaybackRate(rate).
      // Guarded for NullEngine (no real AudioContext).
      try {
        sound.setPlaybackRate(playbackRate);
      } catch {
        // NullEngine / missing AudioContext — ignore silently.
      }
    }
    sound.play();
  }

  private releaseVoice(sound: Sound): void {
    const index = this.activeVoices.findIndex((entry) => entry.sound === sound);
    if (index >= 0) {
      this.activeVoices.splice(index, 1);
    }
  }

  getActiveSfxCount(): number {
    return this.activeVoices.length;
  }

  private registerEventListeners(): void {
    // Player movement
    this.listen(GameEvents.PLAYER_JUMPED, () => {
      this.play("jump");
    });

    this.listen(GameEvents.PLAYER_LANDED, () => {
      this.play("land");
    });

    // Task 7 — footstep SFX with per-cat gain + rate variation.
    // Basil (heavier harness cat): lower pitch (0.80) and slightly louder.
    // Minna (lighter ethereal cat): higher pitch (1.10) and softer.
    this.listen(GameEvents.PLAYER_FOOTSTEP, (data: unknown) => {
      const { cat } = data as { cat: "minna" | "basil" };
      if (cat === "basil") {
        this.playWithOptions("footstep", { gainMultiplier: 1.0, playbackRate: 0.80 });
      } else {
        this.playWithOptions("footstep", { gainMultiplier: 0.65, playbackRate: 1.10 });
      }
    });

    // Shadow Slip enter/exit — stealth whoosh
    this.listen(GameEvents.SHADOW_SLIP_ENTERED, () => {
      this.play("shadow-slip");
    });

    this.listen(GameEvents.SHADOW_SLIP_EXITED, () => {
      this.playWithOptions("shadow-slip", { playbackRate: 1.25 }); // slightly faster on exit
    });

    // Player / harness damage
    this.listen(GameEvents.PLAYER_DAMAGED, (data: unknown) => {
      const { severity } = data as { severity: "light" | "heavy" };
      // Heavy hits are louder; light hits are softer.
      this.playWithOptions("damage", { gainMultiplier: severity === "heavy" ? 1.0 : 0.55 });
    });

    this.listen(GameEvents.HARNESS_DAMAGED, (data: unknown) => {
      const d = data as { amount: number };
      // Map damage amount to gain: bigger hit → louder.
      const gain = Math.min(1.0, 0.4 + (d.amount / 50) * 0.6);
      this.playWithOptions("damage", { gainMultiplier: gain });
    });

    // Harness heal / grease cache — reuse crystal-charge (warm chime sound)
    this.listen(GameEvents.HARNESS_HEALED, () => {
      this.play("crystal-charge");
    });

    this.listen(GameEvents.GREASE_CACHE_CONSUMED, () => {
      this.play("crystal-charge");
    });

    // Abilities
    this.listen(GameEvents.ABILITY_ACTIVATED, (data: unknown) => {
      const { abilityId } = data as { abilityId: string };
      if (abilityId === "calmingResonance") {
        if (this.purrLoop && !this.purrLoop.isPlaying) {
          this.purrLoop.play();
        }
      } else {
        this.play("ability-activate");
      }
    });

    this.listen(GameEvents.ABILITY_DEACTIVATED, (data: unknown) => {
      const { abilityId } = data as { abilityId: string };
      if (abilityId === "calmingResonance") {
        if (this.purrLoop?.isPlaying) {
          this.purrLoop.stop();
        }
      } else {
        this.play("ability-deactivate");
      }
    });

    // Puzzle elements
    this.listen(GameEvents.PRESSURE_PLATE_ACTIVATED, () => {
      this.play("pressure-plate");
    });

    this.listen(GameEvents.DIAL_ALIGNED, () => {
      this.play("dial-aligned");
    });

    this.listen(GameEvents.DIAL_TURNED, () => {
      this.play("dial-spin");
    });

    this.listen(GameEvents.CRYSTAL_ACTIVATED, () => {
      this.play("crystal-charge");
    });

    this.listen(GameEvents.PORTAL_OPENED, () => {
      this.play("portal-open");
    });

    // Puzzle completion
    this.listen(GameEvents.PUZZLE_SOLVED, () => {
      this.play("puzzle-solved");
    });

    // Level 2 Conservatory phase transition — pitch shift ambient to match thermal state
    this.listen(GameEvents.CONSERVATORY_PHASE_CHANGED, (data: unknown) => {
      const { phase } = (data ?? {}) as { phase?: string };
      if (this.ambientLoop) {
        try {
          if (phase === "incubation" || phase === "midnight") {
            this.ambientLoop.setPlaybackRate(0.85);
          } else {
            this.ambientLoop.setPlaybackRate(1.0);
          }
        } catch {
          // NullEngine — ignore
        }
      }
    });


    this.listen(GameEvents.LEVEL_COMPLETE, () => {
      this.play("level-complete");
      this.setMusicDucked(true);
    });

    // Restore music duck after cutscene ends (covers both elder + level-complete flows)
    this.listen(GameEvents.CUTSCENE_ENDED, () => {
      this.setMusicDucked(false);
    });

    // Rhythm game (TurnTableTouch)
    this.listen(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
      this.play("rhythm-hit");
    });

    this.listen(GameEvents.TURN_TABLE_TOUCH_MISS, () => {
      this.play("rhythm-miss");
    });

    // Elder encounters — route per-elder chime via SfxEventMap + duck music
    this.listen(GameEvents.ELDER_ENCOUNTER, (data: unknown) => {
      const { elder } = data as { elder?: string };
      this.play(elderChimeKey(elder));
      this.setMusicDucked(true);
    });

    // Resonant Drop / blockage shatter
    this.listen(GameEvents.BLOCKAGE_SHATTERED, () => {
      this.play("chain-shatter");
    });

    this.listen(GameEvents.SHOCKWAVE_SHATTERED, () => {
      this.play("chain-shatter");
    });

    // Crane lever
    this.listen(GameEvents.CRANE_LEVER_LOCKED, () => {
      this.play("crane-creak");
    });

    // Fox alert / hunting / startled
    this.listen(GameEvents.FOX_ALERT, () => {
      this.play("fox-growl");
    });

    this.listen(GameEvents.FOX_HUNTING, () => {
      this.play("fox-growl");
    });

    this.listen(GameEvents.FOX_STARTLED, () => {
      this.play("fox-growl");
    });

    // Making Biscuits mini-game
    this.listen(GameEvents.BISCUITS_BEAT, () => {
      this.play("baking-ding");
    });

    this.listen(GameEvents.BISCUITS_COMPLETED, () => {
      this.play("baking-ding");
    });

    // Per-species NPC calm SFX
    this.listen(GameEvents.NPC_CALMED, (data: unknown) => {
      const { species } = data as { species?: string };
      this.play(speciesSfxForCalm(species ?? ""));
    });

    // Ability blocked
    this.listen(GameEvents.ABILITY_BLOCKED, (data: unknown) => {
      const { reason } = data as { reason: string };
      this.play(abilityBlockedSfxKey(reason));
    });

    // Crow fed
    this.listen(GameEvents.CROW_FED, () => {
      this.play("crow-caw");
    });

    // Opossum starts following
    this.listen(GameEvents.OPOSSUM_FOLLOWING, () => {
      this.play("opossum-squeak");
    });

    // Co-op abilities
    this.listen(GameEvents.RESONANT_DROP_TRIGGERED, () => {
      this.play("resonant-drop");
    });

    this.listen(GameEvents.CHRONOS_BUBBLE_TRIGGERED, () => {
      this.play("chronos-bubble");
    });

    // Gramophone lullaby — fires once per Basil activation; AudioManager
    // owns the playback so the Gramophone interactive doesn't have to hold
    // a Babylon.Sound ref of its own.
    this.listen(GameEvents.GRAMOPHONE_PLAYED, () => {
      this.play("gramophone-lullaby");
    });

    // Volume persistence — live updates from the settings UI
    this.listen(GameEvents.VOLUME_CHANGED, (data: unknown) => {
      const { master, sfx, music } = data as { master: number; sfx: number; music: number };
      this.setMasterVolume(master);
      this.setSfxVolume(sfx);
      this.setMusicVolume(music);
    });

    // Synergy events
    this.listen(GameEvents.SYNERGY_ENTERED, () => {
      this.synergyTargetVolume = this.ambientVolume * 1.5;
      if (this.synergyLoop && !this.synergyLoop.isPlaying) {
        this.synergyLoop.setVolume(0.0);
        this.synergyLoop.play();
      }
    });

    this.listen(GameEvents.SYNERGY_EXITED, () => {
      this.synergyTargetVolume = 0.0;
    });

    // Wire volume crossfader to the scene update loop
    const obs = this.scene.onBeforeRenderObservable.add(() => {
      if (!this.synergyLoop) return;
      const dt = this.scene.getEngine().getDeltaTime() / 1000;
      const currentVol = this.scene.getEngine().name === "NullEngine" ? 0 : this.synergyLoop.getVolume();
      const diff = this.synergyTargetVolume - currentVol;

      if (Math.abs(diff) > 0.001) {
        const step = diff * 5 * dt;
        this.synergyLoop.setVolume(currentVol + step);
      } else {
        this.synergyLoop.setVolume(this.synergyTargetVolume);
        if (this.synergyTargetVolume === 0 && this.synergyLoop.isPlaying) {
          this.synergyLoop.stop();
        }
      }
    });

    this.unsubscribers.push(() => {
      this.scene.onBeforeRenderObservable.remove(obs);
    });
  }

  /** Helper to subscribe to an event and track the unsubscribe handle. */
  private listen(event: string, callback: (...args: unknown[]) => void): void {
    const unsub = gameEvents.on(event, callback);
    this.unsubscribers.push(unsub);
  }
}
